/**
 * MODULE: platform/browser/frontend/components/CrmAutomateInBrowserButton.tsx
 * PURPOSE: CRM drawer control — open AI browser with record context and a mission preset.
 */

import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import type { BrowserMissionId } from '../data/browserMissions';
import { openBrowserWithLaunch } from '../utils/browserLaunch';

export type CrmAutomateInBrowserButtonProps = {
  mission: BrowserMissionId;
  task: string;
  context?: string;
  customStartUrl?: string;
  disabled?: boolean;
  dataTestId: string;
  label?: string;
};

export function CrmAutomateInBrowserButton({
  mission,
  task,
  context,
  customStartUrl,
  disabled,
  dataTestId,
  label = 'Automate',
}: CrmAutomateInBrowserButtonProps) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      data-testid={dataTestId}
      disabled={disabled}
      title="Automate in AI Browser"
      aria-label="Automate in AI Browser"
      onClick={() =>
        openBrowserWithLaunch(
          {
            mission,
            task,
            context,
            customStartUrl,
            autoRun: true,
          },
          navigate,
        )
      }
      className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2 text-sky-900 transition-transform hover:bg-sky-100 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:border-sky-800/80 dark:bg-sky-950/40 dark:text-sky-100 dark:hover:bg-sky-900/50"
    >
      <GlobeAltIcon className="h-5 w-5 shrink-0" aria-hidden />
      <span className="hidden text-xs font-semibold sm:inline">{label}</span>
    </button>
  );
}
